import React, { useState, Fragment } from "react";
import EyeIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import IconButtonIcons from "../../Common/IconButtonIcons";
import { OfflinePaymentModal } from "../../Common/OfflinePaymentModal";
import TableCommon from "../../Common/Table";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
    useAddWalletCreditMutation,
    useInitiateRefundMutation,
    useDeleteMembersMutation,
    useSwitchMemberMutation,
} from "../../../store/members/membersApis";
import { handleDateTimeDefault, parseStringDate } from "../../../helpers/utils";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import { MemberHistoryModal } from "../../Common/MemberHistoryModal";
import { isAuth } from "../../../helpers/cookies";
import { parseISO } from "date-fns";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import HasPermission from "../../Common/HasPermission";
import { PERMISSIONS } from "../../../helpers/constants";
import { setSnackBar } from "../../../store/common/commonSlice";
import { useDispatch } from "react-redux";

const MembersTableComponent = ({
    loading,
    fetching = false,
    count,
    data,
    edit,
    convert,
    pagination,
    handlePagination,
    generateCard,
    getActivityList,
    getMembersList,
}) => {

    const dispatch = useDispatch();

    const [handleDelete] = useDeleteMembersMutation();
    const [
        switchMember,
        { isLoading: switchMemberLoading },
    ] = useSwitchMemberMutation();
    const [addWalletCredit, { isLoading: walletCreditLoading }] =
        useAddWalletCreditMutation();
    
    const [initiateRefund, { isLoading: refundLoading }] =
        useInitiateRefundMutation();

    const [showPayment, setShowPayment] = useState(false);
    const [showSwitchDialog, setShowSwitchDialog] = useState(false);
    const [selectedSwitchMember, setSelectedSwitchMember] = useState(null);
    const [selectedSecondaryId, setSelectedSecondaryId] = useState("");
    const [showHistory, setShowHistory] = useState(false);
    const [paymentInitialValue, setPaymentInitialValue] = useState({});

    const [showWalletCredit, setShowWalletCredit] = useState(false);
    const [selectedWalletMember, setSelectedWalletMember] = useState(null);
    const [walletCreditAmount, setWalletCreditAmount] = useState("");
    const [walletCreditReason, setWalletCreditReason] = useState("");
    const [walletCreditType, setWalletCreditType] = useState("CREDIT");

    const [showRefund, setShowRefund] = useState(false);
    const [selectedRefundMember, setSelectedRefundMember] = useState(null);
    const [refundAmount, setRefundAmount] = useState("");
    const [refundMode, setRefundMode] = useState("Bank Transfer");
    const [refundDate, setRefundDate] = useState("");
    const [refundReason, setRefundReason] = useState("");
    const [refundTransactionId, setRefundTransactionId] = useState("");
    const [refundCharges, setRefundCharges] = useState("");

    let columns = [
        {
            title: "",
        },
        {
            title: "Sr. No.",
            sort: false,
            minWidth: 50,
        },
        {
            title: "Photo",
            sort: false,
            minWidth: 50,
        },
        {
            title: "Member Id",
            field: "member_id",
            sort: true,
            minWidth: 50,
        },
        {
            title: "Personal Details",
            sort: false,
            minWidth: 170,
        },
        {
            title: "CHSS",
            sort: false,
            minWidth: 170,
        },
        {
            title: "Plan",
            sort: true,
            field: "current_plan.plan_name",
            minWidth: 170,
        },
        {
            title: "Wallet Balance",
            sort: false,
            minWidth: 120,
        },
        ...(isAuth()?.roles === "super"
            ? [
                  {
                      title: "Last Payment Date",
                      sort: false,
                      minWidth: 120,
                  },
              ]
            : []),
        {
            title: "Created At",
            field: "createdAt",
            sort: true,
            minWidth: 90,
        },
        {
            title: "Action",
            name: "",
            sort: false,
            minWidth: 200,
        },
    ];

    const deleteManage = (_id, converted) => {
        if (window.confirm("Are you sure you want to delete?")) {
            if (converted) {
                alert("This member is converted to user, kindly delete the user first and then member.");
                return false;
            }
            handleDelete({ _id });
        }
    };

    const handleWalletCreditModal = (row) => {
        setSelectedWalletMember(row);
        setWalletCreditAmount("");
        setWalletCreditReason("");
        setWalletCreditType("CREDIT");
        setShowWalletCredit(true);
    };

    const handleRefundModal = (row) => {
        setSelectedRefundMember(row);
        setRefundAmount("");
        setRefundCharges("");
        setRefundMode("Bank Transfer");
        setRefundDate(new Date().toISOString().split("T")[0]);
        setRefundReason("");
        setRefundTransactionId("");
        setShowRefund(true);
    };

    const handleWalletCreditSubmit = async () => {
        try {
            const amount = Number(walletCreditAmount);

            const currentBalance = Number(
                selectedWalletMember?.wallet_balance || 0
            );

            if (!Number.isFinite(amount) || amount <= 0) {
                alert("Please enter a valid amount greater than 0.");
                return;
            }

            if (
                walletCreditType === "DEBIT" &&
                amount > currentBalance
            ) {
                alert(
                    `You cannot deduct more than the current wallet balance of ₹${currentBalance.toFixed(
                        2
                    )}.`
                );
                return;
            }

            await addWalletCredit({
                member_id: selectedWalletMember?._id,
                amount,
                type: walletCreditType,
                reason: walletCreditReason,
            }).unwrap();

            setShowWalletCredit(false);
            setSelectedWalletMember(null);
            setWalletCreditAmount("");
            setWalletCreditReason("");
            setWalletCreditType("CREDIT");
        } catch (error) {
            alert(
                error?.data?.message ||
                    error?.message ||
                    "Failed to update wallet balance."
            );
        }
    };

    const handleRefundSubmit = async () => {
        try {
            const amount = Number(refundAmount);
            const charges = Number(refundCharges || 0);

            const walletBalance = Number(
                selectedRefundMember?.wallet_balance || 0
            );

            const totalAmountRefunded = amount - charges;

            if (!Number.isFinite(amount) || amount <= 0) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please enter a valid refund amount greater than 0.",
                        severity: "error",
                    })
                );
                return;
            }

            if (amount > walletBalance) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: `Refund amount cannot exceed wallet balance of ₹${walletBalance.toFixed(
                            2
                        )}.`,
                        severity: "error",
                    })
                );
                return;
            }

            if (!Number.isFinite(charges) || charges < 0) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please enter valid charges.",
                        severity: "error",
                    })
                );
                return;
            }

            if (charges > amount) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message:
                            "Charges cannot be greater than the amount to refund.",
                        severity: "error",
                    })
                );
                return;
            }

            if (totalAmountRefunded <= 0) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message:
                            "Total amount refunded must be greater than 0.",
                        severity: "error",
                    })
                );
                return;
            }

            if (!refundMode) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please select refund mode.",
                        severity: "error",
                    })
                );
                return;
            }

            if (!refundDate) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please select refund date.",
                        severity: "error",
                    })
                );
                return;
            }

            if (!refundReason.trim()) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please enter reason for refund.",
                        severity: "error",
                    })
                );
                return;
            }

            if (!refundTransactionId.trim()) {
                dispatch(
                    setSnackBar({
                        open: true,
                        message: "Please enter transaction ID.",
                        severity: "error",
                    })
                );
                return;
            }

            await initiateRefund({
                member_ref: selectedRefundMember?._id,

                // Amount deducted from wallet
                amount,

                // Charges taken from refund amount
                charges,

                // Actual amount refunded to main account
                refunded_amount: totalAmountRefunded,

                refund_mode: refundMode,
                refund_date: refundDate,
                reason: refundReason.trim(),
                transaction_id: refundTransactionId.trim(),
            }).unwrap();

            dispatch(
                setSnackBar({
                    open: true,
                    message: `Refund initiated successfully. ₹${amount.toFixed(
                        2
                    )} debited from wallet and ₹${totalAmountRefunded.toFixed(
                        2
                    )} refunded.`,
                    severity: "success",
                })
            );

            setShowRefund(false);
            setSelectedRefundMember(null);
            setRefundAmount("");
            setRefundCharges("");
            setRefundMode("Bank Transfer");
            setRefundDate("");
            setRefundReason("");
            setRefundTransactionId("");
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message:
                        error?.data?.message ||
                        error?.message ||
                        "Failed to initiate refund.",
                    severity: "error",
                })
            );
        }
    };

    const handlePaymentModal = (row) => {
        setShowPayment(true);

        setPaymentInitialValue({
            member_id: row._id,
            payment_file: "",
            amount_paid: row?.current_plan?.final_amount,
            plan_id: row.current_plan?.plan_id,
            payment_status: "Success",
            payment_verified: true,
            remarks: "",
            payment_mode: "Cheque",
            createdAt: "",
        });
    };

    const handleSwitchDialogOpen = (row) => {
        setSelectedSwitchMember(row);
        setSelectedSecondaryId("");
        setShowSwitchDialog(true);
    };

    const handleSwitchSubmit = async () => {
        if (!selectedSwitchMember?._id || !selectedSecondaryId) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Please select a secondary member to switch.",
                    severity: "error",
                })
            );
            return;
        }

        const secondary = selectedSwitchMember?.family_details?.find(
            (member) =>
                String(member?._id) === String(selectedSecondaryId) ||
                String(member?.id) === String(selectedSecondaryId)
        );

        if (!secondary) {
            dispatch(
                setSnackBar({
                    open: true,
                    message: "Selected secondary member was not found.",
                    severity: "error",
                })
            );
            return;
        }

        if (
            !window.confirm(
                `Switch the primary member "${selectedSwitchMember.name}" with "${secondary.name}"?`
            )
        ) {
            return;
        }

        try {
            await switchMember({
                member_id: selectedSwitchMember._id,
                secondary_member_id: selectedSecondaryId,
            }).unwrap();

            dispatch(
                setSnackBar({
                    open: true,
                    message: "Member switch completed successfully.",
                    severity: "success",
                })
            );

            setShowSwitchDialog(false);
            setSelectedSwitchMember(null);
            setSelectedSecondaryId("");
        } catch (error) {
            dispatch(
                setSnackBar({
                    open: true,
                    message:
                        error?.data?.message ||
                        error?.message ||
                        "Failed to switch members.",
                    severity: "error",
                })
            );
        }
    };

    const renderTableData =
        !loading && data && data.length > 0 ? (
            data.map(function (row, index) {
                const endDate = parseStringDate(row?.current_plan?.end_date);
                const planExpired = endDate ? endDate < new Date() : true;

                return (
                    <ExpandableTableRow
                        key={row._id}
                        expandComponent={<ExtraComponent data={row} />}
                        sx={{
                            "td, th": {
                                borderBottom: 1,
                                borderBottomColor: "divider",
                                padding: "10px",
                                cursor: "pointer",
                            },
                        }}
                    >
                        <TableCell align="center">{index + 1 + pagination.pageNo * pagination.limit}</TableCell>
                        <TableCell align="center">
                            <img src={row.profile} alt="Profile" width={50} style={{ borderRadius: "50%" }} />
                        </TableCell>
                        <TableCell align="center">
                            <Button variant="outlined" onClick={() => setShowHistory(row.member_id)}>
                                {row.member_id}
                            </Button>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2">{row.name}</Typography>
                            <Typography variant="subtitle2">{row.email}</Typography>
                            <Typography variant="subtitle2">{row.mobile}</Typography>
                            <Typography variant="subtitle2">Gender: {row.gender}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2">{row?.chss_number}</Typography>
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2">{row?.current_plan?.plan_name || "-"}</Typography>
                            <Chip
                                color={!planExpired && row?.fees_paid ? "success" : "error"}
                                label={!planExpired && row?.fees_paid ? "Active & Paid" : "Expired / Not Paid"}
                            />
                        </TableCell>
                        <TableCell align="center">
                            <Typography variant="subtitle2">
                                ₹{Number(row?.wallet_balance || 0).toFixed(2)}
                            </Typography>
                        </TableCell>
                        {isAuth()?.roles === "super" && (
                            <TableCell align="center">
                                <Typography variant="subtitle2">
                                    {row?.last_payment_date ? handleDateTimeDefault(row?.last_payment_date) : "-"}
                                </Typography>
                            </TableCell>
                        )}
                        <TableCell align="center">
                            <Typography variant="subtitle2">{handleDateTimeDefault(row?.createdAt)}</Typography>
                            <Typography variant="subtitle2">{handleDateTimeDefault(row?.convertedAt)}</Typography>
                        </TableCell>
                        <TableCell align="center" component="th" scope="row">
                            <HasPermission permission={PERMISSIONS.MEMBERS.UPDATE} fallback={null}>
                                <IconButtonIcons
                                    title="Edit"
                                    IconComponent={EditIcon}
                                    color="warning"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        edit(row, "Edit");
                                    }}
                                />
                            </HasPermission>
                            <HasPermission permission={PERMISSIONS.MEMBERS.READ} fallback={null}>
                                <IconButtonIcons
                                    title="View"
                                    IconComponent={EyeIcon}
                                    color="info"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        edit(row, "View");
                                    }}
                                />
                            </HasPermission>
                            <HasPermission permission={PERMISSIONS.MEMBERS.CONVERT_TO_USER} fallback={null}>
                                {!row.converted && (
                                    <IconButtonIcons
                                        title="Convert to User"
                                        IconComponent={AutorenewIcon}
                                        color="success"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            convert(row._id);
                                        }}
                                    />
                                )}
                            </HasPermission>
                            <HasPermission permission={PERMISSIONS.MEMBERS.GENERATE_CARD} fallback={null}>
                                <IconButtonIcons
                                    title="Generate Card"
                                    IconComponent={NoteAddIcon}
                                    color="success"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        generateCard(row);
                                    }}
                                />
                            </HasPermission>
                            <HasPermission permission={PERMISSIONS.MEMBERS.UPDATE} fallback={null}>
                                {!row.fees_paid && (
                                    <IconButtonIcons
                                        title="Pay Fees"
                                        IconComponent={CurrencyRupeeIcon}
                                        color="success"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handlePaymentModal(row);
                                        }}
                                    />
                                )}
                            </HasPermission>
                            {isAuth()?.roles === "super" && (
                            <HasPermission
                                permission={PERMISSIONS.MEMBERS.UPDATE}
                                fallback={null}
                            >
                                <IconButtonIcons
                                    title="Add Wallet Credit"
                                    IconComponent={AccountBalanceWalletIcon}
                                    color="success"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleWalletCreditModal(row);
                                    }}
                                />

                                <IconButtonIcons
                                    title="Initiate Refund to Main Account"
                                    IconComponent={CurrencyExchangeIcon}
                                    color="warning"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleRefundModal(row);
                                    }}
                                />
                            </HasPermission>
                            )}
                            {isAuth()?.roles === "super" && (
                            <HasPermission
                                permission={PERMISSIONS.MEMBERS.UPDATE}
                                fallback={null}
                            >
                                {Array.isArray(row?.family_details) &&
                                    row.family_details.length > 0 && (
                                        <IconButtonIcons
                                            title="Switch User"
                                            IconComponent={SwapHorizIcon}
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSwitchDialogOpen(row);
                                            }}
                                        />
                                    )}
                            </HasPermission>
                            )}
                        </TableCell>
                    </ExpandableTableRow>
                );
            })
        ) : (
            <TableRow sx={{ "td, th": { border: 0, padding: "10px" } }}>
                <TableCell colSpan={10} align="center">
                    Data not found
                </TableCell>
            </TableRow>
        );

    return (
        <Fragment>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                count={count}
                loading={loading || fetching}
                pagination={pagination}
                handlePagination={handlePagination}
            />
            <OfflinePaymentModal show={showPayment} close={() => setShowPayment(false)} data={paymentInitialValue} />
            <Dialog
                open={showSwitchDialog}
                onClose={() => {
                    if (!switchMemberLoading) {
                        setShowSwitchDialog(false);
                        setSelectedSwitchMember(null);
                        setSelectedSecondaryId("");
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Switch User</DialogTitle>

                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Primary Member: {selectedSwitchMember?.name} (
                        {selectedSwitchMember?.member_id})
                    </Typography>

                    <Typography sx={{ mb: 2 }}>
                        Select the secondary member who will become the primary member.
                    </Typography>

                    <TextField
                        select
                        SelectProps={{ native: true }}
                        fullWidth
                        value={selectedSecondaryId}
                        onChange={(e) => setSelectedSecondaryId(e.target.value)}
                        disabled={switchMemberLoading}
                    >
                        <option value="" disabled>
                            Select Secondary Member
                        </option>

                        {(selectedSwitchMember?.family_details || []).map(
                            (member) => (
                                <option
                                    key={member._id || member.id}
                                    value={member._id || member.id}
                                >
                                    {member.id} - {member.name}
                                </option>
                            )
                        )}
                    </TextField>
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => {
                            setShowSwitchDialog(false);
                            setSelectedSwitchMember(null);
                            setSelectedSecondaryId("");
                        }}
                        disabled={switchMemberLoading}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSwitchSubmit}
                        disabled={!selectedSecondaryId || switchMemberLoading}
                    >
                        {switchMemberLoading ? "Switching..." : "Confirm Switch"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showWalletCredit}
                onClose={() => {
                    if (!walletCreditLoading) {
                        setShowWalletCredit(false);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Wallet Balance Adjustment
                </DialogTitle>

                <DialogContent>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Member: {selectedWalletMember?.member_id} -{" "}
                        {selectedWalletMember?.name}
                    </Typography>

                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 2,
                            fontWeight: 700,
                            fontSize: "16px",
                        }}
                    >
                        Current Wallet Balance: ₹
                        {Number(selectedWalletMember?.wallet_balance || 0).toFixed(2)}
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        label="Action"
                        value={walletCreditType}
                        onChange={(e) => {
                            setWalletCreditType(e.target.value);
                            setWalletCreditAmount("");
                        }}
                        SelectProps={{
                            native: true,
                        }}
                        sx={{ mb: 2 }}
                    >
                        <option value="CREDIT">
                            Add Credit
                        </option>
                        <option value="DEBIT">
                            Deduct Balance
                        </option>
                    </TextField>

                    <TextField
                        fullWidth
                        type="number"
                        label={
                            walletCreditType === "CREDIT"
                                ? "Credit Amount"
                                : "Deduct Amount"
                        }
                        value={walletCreditAmount}
                        onChange={(e) =>
                            setWalletCreditAmount(e.target.value)
                        }
                        sx={{ mb: 1 }}
                        inputProps={{
                            min: 1,
                            max:
                                walletCreditType === "DEBIT"
                                    ? Number(
                                        selectedWalletMember?.wallet_balance || 0
                                    )
                                    : undefined,
                        }}
                    />

                    {walletCreditType === "DEBIT" && (
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 2 }}
                        >
                            New Wallet Balance: ₹
                            {Math.max(
                                Number(
                                    selectedWalletMember?.wallet_balance || 0
                                ) - Number(walletCreditAmount || 0),
                                0
                            ).toFixed(2)}
                        </Typography>
                    )}

                    {walletCreditType === "DEBIT" && (
                        <Button
                            size="small"
                            variant="outlined"
                            sx={{ mb: 2 }}
                            onClick={() =>
                                setWalletCreditAmount(
                                    String(
                                        Number(
                                            selectedWalletMember?.wallet_balance || 0
                                        )
                                    )
                                )
                            }
                        >
                            Make Wallet Zero
                        </Button>
                    )}

                    <TextField
                        fullWidth
                        label="Reason"
                        value={walletCreditReason}
                        onChange={(e) =>
                            setWalletCreditReason(e.target.value)
                        }
                        multiline
                        rows={3}
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setShowWalletCredit(false)}
                        disabled={walletCreditLoading}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        onClick={handleWalletCreditSubmit}
                        disabled={walletCreditLoading}
                    >
                        {walletCreditLoading
                            ? "Processing..."
                            : walletCreditType === "CREDIT"
                            ? "Add Credit"
                            : "Deduct Balance"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={showRefund}
                onClose={() => {
                    if (!refundLoading) {
                        setShowRefund(false);
                    }
                }}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    Initiate Refund to Main Account
                </DialogTitle>

                <DialogContent>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Member: {selectedRefundMember?.member_id} -{" "}
                        {selectedRefundMember?.name}
                    </Typography>

                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 2 }}
                    >
                        Current Wallet Balance: ₹
                        {Number(
                            selectedRefundMember?.wallet_balance || 0
                        ).toFixed(2)}
                    </Typography>

                    <TextField
                        fullWidth
                        type="number"
                        label="Amount to Refund"
                        value={refundAmount}
                        onChange={(e) =>
                            setRefundAmount(e.target.value)
                        }
                        sx={{ mb: 2 }}
                        inputProps={{
                            min: 1,
                            max: Number(
                                selectedRefundMember?.wallet_balance || 0
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        type="number"
                        label="Charges"
                        value={refundCharges}
                        onChange={(e) => setRefundCharges(e.target.value)}
                        sx={{ mb: 2 }}
                        inputProps={{
                            min: 0,
                        }}
                    />

                    <Typography
                        variant="subtitle1"
                        sx={{
                            mb: 2,
                            fontWeight: 600,
                        }}
                    >
                        Total Amount Refunded: ₹
                        {(
                            Math.max(
                                Number(refundAmount || 0) - Number(refundCharges || 0),
                                0
                            )
                        ).toFixed(2)}
                    </Typography>

                    <TextField
                        select
                        fullWidth
                        label="Refund Mode"
                        value={refundMode}
                        onChange={(e) =>
                            setRefundMode(e.target.value)
                        }
                        SelectProps={{
                            native: true,
                        }}
                        sx={{ mb: 2 }}
                    >
                        <option value="Bank Transfer">
                            Bank Transfer
                        </option>
                        <option value="UPI">UPI</option>
                        <option value="Cheque">Cheque</option>
                        <option value="Cash">Cash</option>
                        <option value="Other">Other</option>
                    </TextField>

                    <TextField
                        fullWidth
                        type="date"
                        label="Refund Date"
                        value={refundDate}
                        onChange={(e) =>
                            setRefundDate(e.target.value)
                        }
                        InputLabelProps={{
                            shrink: true,
                        }}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Reason of Refund"
                        value={refundReason}
                        onChange={(e) =>
                            setRefundReason(e.target.value)
                        }
                        multiline
                        rows={3}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        fullWidth
                        label="Transaction ID"
                        value={refundTransactionId}
                        onChange={(e) =>
                            setRefundTransactionId(e.target.value)
                        }
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setShowRefund(false)}
                        disabled={refundLoading}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleRefundSubmit}
                        disabled={refundLoading}
                    >
                        {refundLoading
                            ? "Processing..."
                            : "Refund"}
                    </Button>
                </DialogActions>
            </Dialog>
            <MemberHistoryModal
                show={showHistory}
                close={() => setShowHistory(false)}
                getActivityList={getActivityList}
                getMembersList={getMembersList}
            />
        </Fragment>
    );
};

export default MembersTableComponent;

const ExpandableTableRow = ({ children, expandComponent, ...otherProps }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);

    return (
        <>
            <TableRow {...otherProps} onClick={() => setIsExpanded(!isExpanded)}>
                <TableCell padding="checkbox">
                    <IconButton onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                {children}
            </TableRow>
            {isExpanded && (
                <TableRow>
                    <TableCell padding="checkbox" />
                    {expandComponent}
                </TableRow>
            )}
        </>
    );
};

const ExtraComponent = ({ data }) => {
    return (
        <TableCell colSpan={10}>
            {data?.family_details?.length ? (
                <div>
                    <Typography variant="h6">Family Members</Typography>
                    {data.family_details.map((member, idx) => (
                        <div key={idx} style={{ marginBottom: "10px" }}>
                            <Typography variant="subtitle2">
                                <strong>Id:</strong> {member.id}
                            </Typography>
                            <Typography variant="subtitle2">
                                <strong>Name:</strong> {member.name}
                            </Typography>
                            <Typography variant="subtitle2">
                                <strong>Gender:</strong> {member.gender}
                            </Typography>
                            <Typography variant="subtitle2">
                                <strong>Relation:</strong> {member.relation}
                            </Typography>
                            <Typography variant="subtitle2">
                                <strong>DOB:</strong> {handleDateTimeDefault(member.dob)}
                            </Typography>
                            {member?.plans?.end_date && (
                                <Fragment>
                                    <Typography variant="subtitle2">
                                        <strong>Plan Name:</strong> {member?.plans?.plan_name}
                                    </Typography>
                                    <Typography variant="subtitle2">
                                        <strong>Plan Expire on:</strong> {member?.plans?.end_date}
                                    </Typography>
                                </Fragment>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <Typography variant="subtitle2">No family details</Typography>
            )}
        </TableCell>
    );
};
